/**
 * Retailer Onboarding Wizard
 * 
 * Multi-step onboarding flow for retailers:
 * 1. Business Information
 * 2. Store Hours
 * 3. Bank/Payout Details (Stripe Connect)
 * 4. Document Verification
 * 5. Store Logo
 * 6. Delivery Configuration
 * 7. Review & Submit
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Building2,
  Clock,
  CreditCard,
  FileText,
  Image,
  Truck,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Loader2
} from 'lucide-react';

// Step components
import BusinessInfoStep from './onboarding/BusinessInfoStep';
import StoreHoursStep from './onboarding/StoreHoursStep';
import BankDetailsStep from './onboarding/BankDetailsStep';
import DocumentsStep from './onboarding/DocumentsStep';
import StoreLogoStep from './onboarding/StoreLogoStep';
import DeliveryConfigStep from './onboarding/DeliveryConfigStep';
import ReviewStep from './onboarding/ReviewStep';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'business_info', label: 'Business Info', icon: Building2, required: true },
  { id: 'store_hours', label: 'Store Hours', icon: Clock, required: true },
  { id: 'bank_details', label: 'Bank Details', icon: CreditCard, required: true },
  { id: 'documents', label: 'Documents', icon: FileText, required: false },
  { id: 'store_logo', label: 'Store Logo', icon: Image, required: false },
  { id: 'delivery_config', label: 'Delivery', icon: Truck, required: true },
  { id: 'review', label: 'Review', icon: CheckCircle, required: true }
];

export default function RetailerOnboardingWizard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statusRes, dataRes] = await Promise.all([
        fetch(`${API_URL}/api/retailer/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/retailer/onboarding/data`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statusRes.ok && dataRes.ok) {
        const statusData = await statusRes.json();
        const onboardingData = await dataRes.json();
        setStatus(statusData);
        setData(onboardingData);

        // Set current step based on status
        const stepMap = {
          'business_info': 0,
          'store_hours': 1,
          'bank_details': 2,
          'documents': 3,
          'store_logo': 4,
          'delivery_config': 5,
          'review': 6,
          'submitted': 6,
          'approved': 6
        };
        setCurrentStepIndex(stepMap[statusData.current_step] || 0);
      }
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
      toast.error('Failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = async (stepId, stepData) => {
    setSaving(true);
    try {
      // Refresh status after step completion
      await fetchOnboardingStatus();
      
      // Move to next step
      if (currentStepIndex < STEPS.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
      
      toast.success('Progress saved!');
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const goToStep = (index) => {
    // Allow going to completed steps or current step
    if (index <= currentStepIndex || status?.steps_completed?.includes(STEPS[index].id)) {
      setCurrentStepIndex(index);
    }
  };

  const renderStepContent = () => {
    const step = STEPS[currentStepIndex];
    const props = {
      data: data?.[step.id.replace('_', '')] || data?.[step.id],
      onComplete: (stepData) => handleStepComplete(step.id, stepData),
      onBack: currentStepIndex > 0 ? () => setCurrentStepIndex(currentStepIndex - 1) : null,
      status,
      allData: data
    };

    switch (step.id) {
      case 'business_info':
        return <BusinessInfoStep {...props} data={data?.business_info} />;
      case 'store_hours':
        return <StoreHoursStep {...props} data={data?.store_hours} />;
      case 'bank_details':
        return <BankDetailsStep {...props} data={data?.bank_details} />;
      case 'documents':
        return <DocumentsStep {...props} data={data?.documents} />;
      case 'store_logo':
        return <StoreLogoStep {...props} data={data?.store_logo} />;
      case 'delivery_config':
        return <DeliveryConfigStep {...props} data={data?.delivery_config} />;
      case 'review':
        return <ReviewStep {...props} allData={data} status={status} />;
      default:
        return <div>Unknown step</div>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // If already submitted/approved, show status
  if (status?.status === 'pending_approval') {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Under Review</h2>
          <p className="text-gray-600 mb-6">
            Your onboarding application has been submitted and is pending admin approval.
            We'll notify you once it's reviewed.
          </p>
          <Badge variant="outline" className="text-amber-600 border-amber-600">
            Pending Approval
          </Badge>
        </CardContent>
      </Card>
    );
  }

  if (status?.status === 'approved') {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">You're All Set!</h2>
          <p className="text-gray-600 mb-6">
            Your store has been approved. You can now start posting deals!
          </p>
          <Button onClick={() => navigate('/retailer/dashboard')} className="bg-emerald-600 hover:bg-emerald-700">
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Progress Header */}
      <Card className="mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Store Onboarding</CardTitle>
              <CardDescription>Complete these steps to start selling on DealShaq</CardDescription>
            </div>
            <Badge variant="outline" className="text-emerald-600">
              {status?.completion_percentage || 0}% Complete
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Progress value={status?.completion_percentage || 0} className="h-2 mb-4" />
          
          {/* Step indicators */}
          <div className="flex justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isComplete = status?.steps_completed?.includes(step.id);
              const isCurrent = index === currentStepIndex;
              const isAccessible = index <= currentStepIndex || isComplete;
              
              return (
                <button
                  key={step.id}
                  onClick={() => goToStep(index)}
                  disabled={!isAccessible}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                    isCurrent
                      ? 'bg-emerald-100 text-emerald-700'
                      : isComplete
                      ? 'text-emerald-600 hover:bg-emerald-50'
                      : isAccessible
                      ? 'text-gray-500 hover:bg-gray-100'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isComplete
                      ? 'bg-emerald-600 text-white'
                      : isCurrent
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-200'
                  }`}>
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{step.label}</span>
                  {step.required && !isComplete && (
                    <span className="text-[10px] text-red-500 hidden sm:block">Required</span>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Verification Status */}
      {(status && (!status.email_verified || !status.phone_verified)) && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Verification Required</p>
                <p className="text-sm text-amber-700">
                  {!status.email_verified && 'Email verification pending. '}
                  {!status.phone_verified && 'Phone verification pending.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Step Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {(() => {
              const Icon = STEPS[currentStepIndex].icon;
              return <Icon className="w-5 h-5" />;
            })()}
            {STEPS[currentStepIndex].label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>
    </div>
  );
}
